import { MessageMedium } from 'constants/messageMediums';
import { useQueryDragAndDropTemplates } from 'modules/campaigns/lib/hooks/useQueryDragAndDropTemplates';
import { HtmlEditor } from 'modules/graphql';
import { displayContent } from 'modules/templateEditor/common';
import { DRAG_AND_DROP_OPTIONS } from 'modules/templateEditor/common/constants';
import { DragDropTemplateOption, SelectedMessageMedium } from 'modules/templateEditor/common/types';
import { addNegativeToast } from 'modules/toastContainer';
import { Dispatch, SetStateAction, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { P, match } from 'ts-pattern';
import { NoOp } from 'utils/noop';
import { isValidJsObj } from 'utils/stringHelpers';
import { ModalType, useModalContext } from 'modules/templateEditor/context';
import { SelectedContentEditorStep } from '../contexts/SelectContentEditorContext';

export const dragAndDropTemplateToJsonString = (meta?: any) => {
  return !!meta?.json && isValidJsObj(meta?.json) ? JSON.stringify(meta?.json) : '';
};

export const maybeDragDropTemplateMetadata = (options: DragDropTemplateOption[], id: any) =>
  (options.find((opt) => opt.id === id) || {})?.metadata;

type UseSelectContentEditorProps = {
  medium: SelectedMessageMedium;
  saveTemplateEditor: (editorVariables: Record<string, string>, callback?: NoOp) => void;
  setDragAndDropInitValue: (initValue?: string | undefined) => void;
  setSelectedContentEditorStep: Dispatch<SetStateAction<SelectedContentEditorStep>>;
};

export const useSelectContentEditor = ({
  medium,
  saveTemplateEditor,
  setDragAndDropInitValue,
  setSelectedContentEditorStep,
}: UseSelectContentEditorProps) => {
  const dispatch = useDispatch();
  const { closeModal } = useModalContext();

  const {
    dragAndDropTemplates = [],
    isLoading: isLoadingDefaultTemplates,
    refetch: refetchDefaultTemplates,
  } = useQueryDragAndDropTemplates({
    onError: (e) => {
      dispatch(addNegativeToast(displayContent.errDragAndDropTemplates, e));
    },
  });

  const defaultDnDTemplateOptions = useMemo(() => {
    const options: DragDropTemplateOption[] = DRAG_AND_DROP_OPTIONS.map((opt) => {
      const dragAndDropTemplate = dragAndDropTemplates.find((dataOpt) => dataOpt.slug === opt.id);

      return {
        ...opt,
        label: opt.label || dragAndDropTemplate?.templateTitle || '',
        altText: opt.altText || dragAndDropTemplate?.description || '',
        metadata: dragAndDropTemplateToJsonString(dragAndDropTemplate),
      };
    });

    return options;
  }, [dragAndDropTemplates]);

  const selectContentEditor = useCallback(
    (htmlEditor: HtmlEditor, template?: string) => {
      const callbackFn = () => closeModal(ModalType.SelectContentEditorModals);
      const selectHtmlEditor = () => {
        saveTemplateEditor({ htmlEditor }, callbackFn);
      };

      const selectDnDEditor = (metadata?: string) => {
        if (!metadata) {
          dispatch(addNegativeToast(displayContent.errDragAndDropTemplateNA));
        } else {
          setDragAndDropInitValue(metadata); // first render for the editor, so the initial value of the metadata needs to be updated
          saveTemplateEditor({ htmlEditor, beeBuilderMetadata: metadata }, callbackFn);
        }
      };

      match({ htmlEditor, template, medium })
        .with({ htmlEditor: HtmlEditor.Ace }, selectHtmlEditor)
        .with({ htmlEditor: HtmlEditor.Wysiwyg }, selectHtmlEditor)
        .with({ htmlEditor: HtmlEditor.Bee, medium: MessageMedium.InApp }, () => {
          const metadata = maybeDragDropTemplateMetadata(defaultDnDTemplateOptions, 'BF-empty');
          selectDnDEditor(metadata);
        })
        .with({ htmlEditor: HtmlEditor.Bee, template: P.when(() => !template) }, () =>
          setSelectedContentEditorStep(SelectedContentEditorStep.SelectDragAndDropDefaultTemplate),
        )
        .otherwise(() => {
          const metadata = maybeDragDropTemplateMetadata(defaultDnDTemplateOptions, template);
          selectDnDEditor(metadata);
        });
    },
    [defaultDnDTemplateOptions],
  );

  return {
    defaultDnDTemplateOptions,
    isLoadingDefaultTemplates,
    refetchDefaultTemplates,
    selectContentEditor,
  };
};
